import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Calendar, BookOpen, ChefHat, Image as ImgIcon, Users, Heart, Lock } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch { return iso; }
};
const fmtRel = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
};

export default function PublicDigest() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/digest/${token}`);
        if (!res.ok) {
          const detail = (await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`;
          if (!cancelled) setError(detail);
        } else {
          const json = await res.json();
          if (!cancelled) setData(json);
        }
      } catch (e) {
        if (!cancelled) setError("Could not load this digest. Check your connection.");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--hh-cream)" }}>
        <div className="nb-card p-8" data-testid="digest-loading">
          <p className="text-lg" style={{ fontFamily: "var(--hh-display)" }}>Loading family digest…</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--hh-cream)" }}>
        <div className="nb-card max-w-md p-9 text-center" data-testid="digest-error">
          <Lock strokeWidth={2} className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--hh-terracotta)" }} />
          <h1 className="text-3xl font-extrabold mb-2" style={{ fontFamily: "var(--hh-display)" }}>Link unavailable</h1>
          <p className="text-sm" style={{ color: "var(--hh-ink-soft)" }}>{error}</p>
          <p className="text-xs mt-4" style={{ color: "var(--hh-ink-soft)" }}>The family admin can mint a fresh link from their familytable Profile page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--hh-cream)" }} data-testid="public-digest-root">
      {/* Hero */}
      <header className="px-6 sm:px-12 py-10 max-w-5xl mx-auto">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full border text-[11px] font-semibold tracking-wide uppercase"
             style={{ background: "rgba(212,162,76,0.12)", borderColor: "var(--hh-line)", color: "var(--hh-ink-soft)" }}>
          <Heart strokeWidth={2.4} className="w-3 h-3" /> familytable · public digest
        </div>
        <h1 className="text-5xl sm:text-7xl font-extrabold leading-[0.95] tracking-tight"
            style={{ fontFamily: "var(--hh-display)" }} data-testid="digest-family-name">
          The {data.family.name}
        </h1>
        <p className="mt-4 text-base max-w-xl" style={{ color: "var(--hh-ink-soft)" }}>
          A warm window into the family. Read-only — no login needed. Shared with care by the family admin.
        </p>
      </header>

      <main className="px-6 sm:px-12 max-w-5xl mx-auto pb-20 space-y-10">
        {/* Members */}
        <section className="nb-card p-7" data-testid="digest-members">
          <div className="flex items-center gap-2 mb-5">
            <Users strokeWidth={2.2} className="w-5 h-5" style={{ color: "var(--hh-terracotta)" }} />
            <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>Members</h2>
            <span className="ml-auto text-xs" style={{ color: "var(--hh-ink-soft)" }}>{data.members.length}</span>
          </div>
          <div className="flex flex-wrap gap-4">
            {data.members.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 rounded-2xl"
                   style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)" }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold"
                     style={{ background: m.avatar_color || "var(--hh-honey-soft)", color: "var(--hh-ink)", fontFamily: "var(--hh-display)" }}>
                  {(m.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold">{m.name}</div>
                  <div className="text-xs" style={{ color: "var(--hh-ink-soft)" }}>{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Events */}
        {data.events.length > 0 && (
          <section className="nb-card p-7" data-testid="digest-events">
            <div className="flex items-center gap-2 mb-5">
              <Calendar strokeWidth={2.2} className="w-5 h-5" style={{ color: "var(--hh-terracotta)" }} />
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>Coming up</h2>
            </div>
            <ul className="space-y-3">
              {data.events.map((e) => (
                <li key={e.id} className="flex items-center gap-4">
                  <span style={{ background: e.color || "var(--hh-honey)" }} className="w-1.5 h-12 rounded-full shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{e.title}</div>
                    {e.location && <div className="text-xs" style={{ color: "var(--hh-ink-soft)" }}>{e.location}</div>}
                  </div>
                  <div className="text-xs whitespace-nowrap" style={{ color: "var(--hh-ink-soft)" }}>{fmtDate(e.start)}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Journal */}
        {data.journal.length > 0 && (
          <section className="nb-card p-7" data-testid="digest-journal">
            <div className="flex items-center gap-2 mb-5">
              <BookOpen strokeWidth={2.2} className="w-5 h-5" style={{ color: "var(--hh-terracotta)" }} />
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>From the journal</h2>
            </div>
            <div className="space-y-5">
              {data.journal.map((j) => (
                <article key={j.id} className="flex gap-4">
                  {j.photo_url ? (
                    <img src={j.photo_url} alt="" className="w-20 h-20 rounded-2xl object-cover shrink-0"
                         style={{ border: "1px solid var(--hh-line)" }} />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center shrink-0"
                         style={{ background: "var(--hh-honey-soft)", color: "var(--hh-ink)" }}>
                      <BookOpen strokeWidth={2.4} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{j.title}</div>
                    <div className="text-xs mb-1" style={{ color: "var(--hh-ink-soft)" }}>
                      {j.author_name} · {fmtRel(j.created_at)}
                      {j.mood && <span className="ml-2 px-2 py-0.5 rounded-full" style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)" }}>{j.mood}</span>}
                    </div>
                    <p className="text-sm leading-relaxed line-clamp-3" style={{ color: "var(--hh-ink)" }}>{j.content}</p>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Recipes */}
        {data.recipes.length > 0 && (
          <section className="nb-card p-7" data-testid="digest-recipes">
            <div className="flex items-center gap-2 mb-5">
              <ChefHat strokeWidth={2.2} className="w-5 h-5" style={{ color: "var(--hh-terracotta)" }} />
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>From the kitchen</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.recipes.map((r) => (
                <div key={r.id} className="rounded-2xl overflow-hidden" style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)" }}>
                  {r.image_url ? (
                    <img src={r.image_url} alt={r.title} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center" style={{ background: "var(--hh-sage-soft)" }}>
                      <ChefHat strokeWidth={2} className="w-8 h-8" style={{ color: "var(--hh-ink)" }} />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="font-semibold text-sm mb-1">{r.title}</div>
                    <div className="text-xs" style={{ color: "var(--hh-ink-soft)" }}>
                      {r.prep_time}m · serves {r.servings}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Photos */}
        {data.photos.length > 0 && (
          <section className="nb-card p-7" data-testid="digest-photos">
            <div className="flex items-center gap-2 mb-5">
              <ImgIcon strokeWidth={2.2} className="w-5 h-5" style={{ color: "var(--hh-terracotta)" }} />
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>Recent photos</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.photos.map((p) => (
                <figure key={p.id} className="rounded-2xl overflow-hidden" style={{ border: "1px solid var(--hh-line)" }}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt={p.caption || ""} className="w-full h-40 object-cover" loading="lazy" />
                  {p.caption && (
                    <figcaption className="px-3 py-2 text-xs" style={{ background: "var(--hh-card-soft)", color: "var(--hh-ink-soft)" }}>
                      {p.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </section>
        )}
      </main>

      <footer className="px-6 sm:px-12 pb-10 text-center text-xs" style={{ color: "var(--hh-ink-soft)" }}>
        Sent with care by familytable · This link expires {fmtDate(data.expires_at)}.
      </footer>
    </div>
  );
}
